import axios from "axios";

export const API =
  process.env.REACT_APP_API_BASE || "https://image.bvhvgl.com/api";
export const IMAGE_BASE =
  process.env.REACT_APP_IMAGE_BASE || "https://image.bvhvgl.com";

// ===== PHIÊN ĐĂNG NHẬP + MẬT KHẨU THƯ MỤC =====
// - "Vé đăng nhập" (token) do máy chủ cấp khi đăng nhập, để máy chủ biết chắc ai
//   đang gọi: người tạo thư mục và mã số đặc biệt vào thẳng thư mục có mật khẩu.
// - "Vé mở khóa" được cấp sau khi nhập đúng mật khẩu một thư mục; dùng trong 12 giờ
//   và chỉ giữ trong thẻ trình duyệt đang mở (sessionStorage).
const TOKEN_KEY = "token";
const FOLDER_KEYS_KEY = "hvgl_folder_keys";

export const getToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
};

export const setToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* trình duyệt chặn lưu trữ: bỏ qua */ }
};

const readFolderKeys = () => {
  try {
    const all = JSON.parse(sessionStorage.getItem(FOLDER_KEYS_KEY) || "{}");
    const now = Date.now();
    const live = {};
    Object.keys(all).forEach((p) => {
      if (all[p] && all[p].key && all[p].expiresAt > now) live[p] = all[p];
    });
    return live;
  } catch {
    return {};
  }
};

export const saveFolderKey = (lockPath, key, expiresAt) => {
  try {
    const all = readFolderKeys();
    all[lockPath] = { key, expiresAt };
    sessionStorage.setItem(FOLDER_KEYS_KEY, JSON.stringify(all));
  } catch { /* bỏ qua */ }
};

export const clearFolderKeys = () => {
  try { sessionStorage.removeItem(FOLDER_KEYS_KEY); } catch { /* bỏ qua */ }
};

// Header gửi kèm mọi lệnh gọi API.
export const authHeaders = (extra = {}) => {
  const headers = { ...extra };
  const token = getToken();
  if (token) headers["X-Auth-Token"] = token;
  const keys = Object.values(readFolderKeys()).map((k) => k.key);
  if (keys.length > 0) headers["X-Folder-Keys"] = keys.join(",");
  return headers;
};

// App.js gọi một số API bằng axios: gắn sẵn các header trên cho mọi lệnh axios.
axios.interceptors.request.use((config) => {
  const headers = authHeaders();
  Object.keys(headers).forEach((name) => { config.headers[name] = headers[name]; });
  return config;
});

// Vé đăng nhập còn dùng được không. Trả về:
//   { ok: true, maSo, role, superUser }  – còn dùng được
//   { ok: false, expired: true }         – máy chủ không nhận vé: cần đăng nhập lại
//   { ok: false }                        – không hỏi được (mạng lỗi, máy chủ bản cũ): bỏ qua
export async function getMe() {
  try {
    const res = await fetch(`${API}/me`, { headers: authHeaders(), cache: "no-store" });
    if (res.status === 401) return { ok: false, expired: true };
    if (!res.ok) return { ok: false };
    return { ok: true, ...(await res.json()) };
  } catch {
    return { ok: false };
  }
}

const readJson = async (res) => {
  try { return await res.json(); } catch { return {}; }
};

// Nhập mật khẩu để mở thư mục `path`. Đúng thì lưu vé mở khóa và trả về { ok: true }.
export async function unlockFolder(path, password) {
  const res = await fetch(`${API}/folder/unlock`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ path, password }),
  });
  const data = await readJson(res);
  if (res.ok) {
    if (data.key) saveFolderKey(data.lockPath, data.key, data.expiresAt);
    return { ok: true };
  }
  return { ok: false, message: data.message || "Không mở được thư mục." };
}

// Đặt hoặc đổi mật khẩu thư mục.
export async function setFolderLock(path, password, name) {
  const res = await fetch(`${API}/folder/lock`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ path, password, name }),
  });
  const data = await readJson(res);
  return res.ok ? { ok: true } : { ok: false, message: data.message || "Không đặt được mật khẩu." };
}

// Gỡ mật khẩu: thư mục trở lại công khai.
export async function removeFolderLock(path) {
  const res = await fetch(`${API}/folder/lock?path=${encodeURIComponent(path)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  const data = await readJson(res);
  return res.ok ? { ok: true } : { ok: false, message: data.message || "Không gỡ được mật khẩu." };
}


export async function login(username, password) {
  const res = await fetch(`${API}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  return res.json();
}

export async function getFolderContent(path = "", { signal } = {}) {
  const res = await fetch(`${API}/folder?path=${encodeURIComponent(path)}`, {
    signal,
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = new Error(`Không tải được thư mục (HTTP ${res.status})`);
    err.status = res.status;
    if (res.status === 403) {
      // Thư mục có mật khẩu mà chưa mở khóa: báo riêng để hiện ô nhập mật khẩu.
      const data = await readJson(res);
      if (data.error === "locked") {
        err.locked = { lockPath: data.lockPath || path, ownerName: data.ownerName || "" };
      }
    }
    throw err;
  }
  return res.json();
}

export async function createFolder(parent, folderName) {
  const res = await fetch(`${API}/folder/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ parent, folderName }),
  });
  return res.json();
}

export async function deleteFile(path, name) {
  const res = await fetch(
    `${API}/delete-file?path=${encodeURIComponent(path)}&name=${encodeURIComponent(name)}`,
    { method: "DELETE" }
  );
  return res.json();
}

export async function renameFile(path, oldName, newName) {
  const res = await fetch(`${API}/rename-file`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ path, oldName, newName }),
  });
  return res.json();
}

export async function deleteFolder(path) {
  const res = await fetch(
    `${API}/delete-folder?path=${encodeURIComponent(path)}`,
    { method: "DELETE" }
  );
  return res.json();
}

export async function renameFolder(parent, oldName, newName) {
  const res = await fetch(`${API}/rename-folder`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ parent, oldName, newName }),
  });
  return res.json();
}

// ===== COMPRESS =====
export async function compressAllImages(maxMB = 5) {
  const res = await fetch(`${API}/compress-all?maxMB=${maxMB}`, {
    method: "POST",
  });
  if (!res.ok) throw new Error("Compress failed");
  return res.json();
}

// ===== DOWNLOAD =====
export async function downloadFiles(path, files) {
  const res = await fetch(`${API}/download`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ path, files }),
  });
  if (!res.ok) throw new Error("Download failed");

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = files.length === 1 ? files[0] : "download.zip";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ===== USAGE (ảnh nào đang được trang web khác tải) =====
// Máy chủ ghi nhận theo lượt tải thực tế, nên ảnh chưa ai xem thì chưa có bản ghi.
export async function getUsage(path = "", folders = []) {
  const qs = new URLSearchParams({ path });
  folders.forEach((f) => qs.append("folders", f));
  try {
    const res = await fetch(`${API}/usage?${qs.toString()}`, { headers: authHeaders() });
    if (!res.ok) return { files: {}, folders: {} };
    const data = await res.json();
    return { files: data.files || {}, folders: data.folders || {} };
  } catch {
    return { files: {}, folders: {} };
  }
}

// ===== TRASH =====
export async function getTrash() {
  const res = await fetch(`${API}/trash`, { cache: "no-store" });
  if (!res.ok) throw new Error("Load trash failed");
  return res.json();
}

export async function restoreTrash(id) {
  const res = await fetch(`${API}/trash/restore`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
  if (!res.ok) throw new Error("Restore failed");
  return res.json();
}

export async function purgeTrash(id) {
  const res = await fetch(`${API}/trash?id=${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Purge failed");
  return res.json();
}
